"use client";

import type { AccordionCarouselBlock } from "@mw/shared/payload-types";
import { getImageProps, getVideoUrl, isEntity } from "@/app/_utilities/payload";
import Image from "next/image";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import * as m from "motion/react-m";
import { AnimatePresence, MotionConfig, useInView } from "motion/react";
import { useDebounce } from "use-debounce";
import { ChatPlayer, type ChatPlayerProps } from "@ui/components/core/ChatPlayer";
import { Noise } from "@ui/components/core/Noise/Noise";
import { pxToRem } from "@ui/lib/utils";
import clsx from "clsx";
import { sizes } from "@ui/lib/sizes";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { useTranslations } from "next-intl";
import { useHover } from "react-aria";
import { createChatPlayerSourceFromAccordionConversation } from "./accordionCarouselConversation";

type Slide = NonNullable<AccordionCarouselBlock["slides"]>[number];
type ConversationMessages = NonNullable<NonNullable<Slide["overlay"]["conversation"]>["messages"]>;
type ImageAsset = Slide["asset"]["image"];
type VideoAsset = Slide["asset"]["video"];

const DEFAULT_NOISE_OPACITY_PERCENTAGE = 60;
const DESKTOP_MAX_VISIBLE_CHAT_BUBBLES = 4;
const MOBILE_MAX_VISIBLE_CHAT_BUBBLES = 2;

function getCoverObjectPosition(coverPositionX: number | null | undefined) {
  if (typeof coverPositionX !== "number") {
    return undefined;
  }

  const x = Math.max(0, Math.min(100, coverPositionX));
  return `${x}% center`;
}

function getOpacityFromPercentage(value: number | null | undefined, fallback: number) {
  const percentage = typeof value === "number" ? value : fallback;
  return Math.max(0, Math.min(100, percentage)) / 100;
}

function getSlideNoiseOpacity(slide: Slide) {
  return getOpacityFromPercentage(slide.asset.noiseOpacity, DEFAULT_NOISE_OPACITY_PERCENTAGE);
}

function getSlideNoiseVariant(slide: Slide, isVideoPlaying: boolean) {
  switch (slide.asset.template) {
    case "video":
      return isVideoPlaying ? "dynamic" : "static";
    case "image":
      return "static";
  }
}

export function BlockAccordionCarousel({ label, slides }: AccordionCarouselBlock) {
  const t = useTranslations("ClientKeys.BlockAccordionCarousel");
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [debouncedActiveIndex] = useDebounce(activeIndex, 400);
  const containerMetricsRef = useRef<{ scrollWidth: number; clientWidth: number } | null>(null);

  const checkActiveIndex = useCallback(function checkActiveIndex() {
    const container = containerRef.current;
    const metrics = containerMetricsRef.current;
    if (!container || !metrics) {
      return;
    }
    const scrollLeft = container.scrollLeft;
    const scrollWidth = metrics.scrollWidth;
    const clientWidth = metrics.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    const progress = scrollLeft / maxScroll;
    const newActiveIndex = Math.max(0, Math.min(3, Math.round(progress * 3)));
    setActiveIndex(newActiveIndex);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function onResize() {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      containerMetricsRef.current = { scrollWidth, clientWidth };
      checkActiveIndex();
    }

    onResize();

    const observer = new window.ResizeObserver(onResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [checkActiveIndex]);

  if (!slides || slides.length !== 4) {
    return null;
  }

  return (
    <MotionConfig reducedMotion='user'>
      <div
        ref={containerRef}
        aria-roledescription='carousel'
        aria-label={label}
        className='tw-flex lg:tw-h-[37.5rem] tw-gap-x-3 tw-overflow-x-auto lg:tw-overflow-visible tw-snap-x tw-snap-mandatory tw-scrollbar-none -tw-mx-full-bleed lg:tw-mx-0 tw-px-full-bleed lg:tw-px-0 tw-scroll-px-full-bleed lg:tw-scroll-px-0 tw-@container'
        onScroll={() => {
          checkActiveIndex();
        }}
      >
        {slides.map((slide, index) => (
          <Slide
            key={index}
            slide={slide}
            index={index}
            isActive={activeIndex === index && debouncedActiveIndex === index}
            activeIndex={activeIndex}
            hoverIndex={hoverIndex}
            ariaLabel={t("slide_label", { number: index + 1, total: slides.length })}
            onSelect={() => {
              setActiveIndex(index);
              setHoverIndex(null);
            }}
            onHoverStart={() => {
              setHoverIndex(index);
            }}
            onHoverEnd={() => {
              setHoverIndex(null);
            }}
          />
        ))}
      </div>
    </MotionConfig>
  );
}

function SlideImage({ imageAsset }: { imageAsset: ImageAsset }) {
  const image = imageAsset?.image;
  if (!image || !isEntity(image)) {
    return null;
  }
  const imageProps = getImageProps(image, { placeholder: true, fill: true });
  if (!imageProps) {
    return null;
  }
  return (
    <Image
      {...imageProps}
      sizes={sizes(pxToRem(760))}
      objectFit='cover'
      objectPosition={getCoverObjectPosition(imageAsset?.coverPositionX)}
    />
  );
}

function SlideVideo({
  videoAsset,
  isActive,
  onVideoElement,
  onVideoPlayingChange,
}: {
  videoAsset: VideoAsset;
  isActive: boolean;
  onVideoElement?: (element: HTMLVideoElement | null) => void;
  onVideoPlayingChange?: (playing: boolean) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [playing, setPlaying] = useState(false);
  const inView = useInView(containerRef);
  const [wasInView, setWasInView] = useState(false);
  const [wasActive, setWasActive] = useState(false);

  const setVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      videoRef.current = element;
      onVideoElement?.(element);

      if (!element) {
        onVideoPlayingChange?.(false);
      }
    },
    [onVideoElement, onVideoPlayingChange],
  );

  const syncPlaying = useCallback(
    (nextPlaying: boolean) => {
      setPlaying(nextPlaying);
      onVideoPlayingChange?.(nextPlaying);
    },
    [onVideoPlayingChange],
  );

  if (inView && !wasInView) {
    setWasInView(true);
  }

  if (isActive && !wasActive) {
    setWasActive(true);
  }

  const onCanPlayThrough = useCallback(() => {
    setCanPlayThrough(true);
  }, []);

  useEffect(() => {
    if (isActive && videoRef.current && inView) {
      if (canPlayThrough) {
        videoRef.current.play();
      } else {
        videoRef.current.load();
      }
    } else {
      syncPlaying(false);
      videoRef.current?.pause();
    }
  }, [canPlayThrough, inView, isActive, syncPlaying]);

  const url = getVideoUrl(videoAsset?.video);
  if (!url) {
    return null;
  }

  const src = wasActive ? url : undefined;

  const image = videoAsset?.thumbnail;
  const imageProps = getImageProps(image, { placeholder: true, fill: true });
  if (!imageProps) {
    return null;
  }
  const objectPosition = getCoverObjectPosition(videoAsset?.coverPositionX);

  return (
    <div ref={containerRef} className='tw-absolute tw-inset-0'>
      <Image {...imageProps} sizes={sizes(pxToRem(760))} objectFit='cover' objectPosition={objectPosition} />
      <div
        className={clsx(
          "tw-absolute tw-inset-0 tw-transition-opacity tw-duration-300",
          isActive && playing ? "" : "tw-opacity-0",
        )}
      >
        {wasInView && (
          <video
            ref={setVideoRef}
            onCanPlayThrough={onCanPlayThrough}
            onPause={() => syncPlaying(false)}
            onPlaying={() => syncPlaying(true)}
            onWaiting={() => syncPlaying(false)}
            preload='metadata'
            loop
            muted
            playsInline
            src={src}
            className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
            style={objectPosition ? { objectPosition } : undefined}
          />
        )}
      </div>
    </div>
  );
}

function SlideAsset({
  slide,
  isActive,
  onVideoElement,
  onVideoPlayingChange,
}: {
  slide: Slide;
  isActive: boolean;
  onVideoElement?: (element: HTMLVideoElement | null) => void;
  onVideoPlayingChange?: (playing: boolean) => void;
}) {
  const { asset } = slide;

  if (asset.template === "image") {
    return <SlideImage imageAsset={asset.image} />;
  }

  if (asset.template === "video") {
    return (
      <SlideVideo
        videoAsset={asset.video}
        isActive={isActive}
        onVideoElement={onVideoElement}
        onVideoPlayingChange={onVideoPlayingChange}
      />
    );
  }

  return null;
}

type SlideSizes = {
  xs: string;
  sm: string;
  md: string;
  lg: string;
};

function getBaseSlideWidth(index: number, activeIndex: number, slideSizes: SlideSizes) {
  return [
    ["var(--active-width)", slideSizes.md, slideSizes.sm, slideSizes.xs],
    [slideSizes.lg, "var(--active-width)", slideSizes.md, slideSizes.sm],
    [slideSizes.sm, slideSizes.md, "var(--active-width)", slideSizes.lg],
    [slideSizes.xs, slideSizes.sm, slideSizes.md, "var(--active-width)"],
  ][index][activeIndex];
}

function getSlideWidth({
  index,
  activeIndex,
  hoverIndex,
  slideSizes,
  hoverExpansion,
}: {
  index: number;
  activeIndex: number;
  hoverIndex: number | null;
  slideSizes: SlideSizes;
  hoverExpansion: string;
}) {
  const baseWidth = getBaseSlideWidth(index, activeIndex, slideSizes);
  const isHoveringInactive = hoverIndex !== null && hoverIndex !== activeIndex;

  if (!isHoveringInactive) {
    return baseWidth;
  }

  if (index === hoverIndex) {
    return `calc(${baseWidth} + ${hoverExpansion})`;
  }

  const beforeCount = hoverIndex;
  const afterCount = 3 - hoverIndex;
  const shrinkTotal = beforeCount === 0 || afterCount === 0 ? hoverExpansion : `calc(${hoverExpansion} / 2)`;

  if (index < hoverIndex) {
    return `calc(${baseWidth} - ${shrinkTotal} / ${beforeCount})`;
  }

  return `calc(${baseWidth} - ${shrinkTotal} / ${afterCount})`;
}

function Slide({
  slide,
  index,
  onSelect,
  isActive,
  activeIndex,
  ariaLabel,
  hoverIndex,
  onHoverStart,
  onHoverEnd,
}: {
  slide: Slide;
  index: number;
  isActive: boolean;
  onSelect?: () => void;
  activeIndex: number;
  ariaLabel: string;
  hoverIndex: number | null;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  const id = useId();
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const syncConversationToVideo = slide.asset.template === "video";
  const gap = pxToRem(12);
  const hoverExpansion = pxToRem(8);
  const noiseOverlayOpacity = getSlideNoiseOpacity(slide);
  const noiseVariant = getSlideNoiseVariant(slide, isActive && isVideoPlaying);
  const slideSizes: SlideSizes = {
    xs: pxToRem(64),
    sm: pxToRem(96),
    md: pxToRem(142),
    lg: pxToRem(220),
  };
  const width = getSlideWidth({
    index,
    activeIndex,
    hoverIndex,
    slideSizes,
    hoverExpansion,
  });
  const { hoverProps } = useHover({
    onHoverStart() {
      if (!isActive) {
        onHoverStart?.();
      }
    },
    onHoverEnd() {
      onHoverEnd?.();
    },
  });

  return (
    <div
      className='tw-relative tw-isolate tw-flex-none tw-aspect-square sm:tw-aspect-[760/600] lg:tw-aspect-auto max-lg:!tw-w-[min(90%,47.5rem)] tw-snap-start'
      style={
        {
          "--active-width": `calc(100cqw - (${gap} * 3) - ${slideSizes.lg} - ${slideSizes.sm} - ${slideSizes.xs})`,
          width: `calc(${width})`,
        } as React.CSSProperties
      }
    >
      <button
        type='button'
        className='tw-group tw-absolute tw-z-50 tw-inset-y-0 -tw-inset-x-1.5 tw-hidden lg:tw-block tw-outline-none'
        inert={isActive}
        onClick={onSelect}
        {...hoverProps}
        aria-label={ariaLabel}
        aria-controls={`${id}-content`}
        aria-expanded={isActive}
        id={`${id}-button`}
      >
        <div className='tw-absolute tw-inset-y-0 tw-inset-x-1.5 tw-rounded-4xl group-focus-visible:tw-outline-focus' />
      </button>

      <m.div layout className='tw-absolute tw-inset-0 tw-bg-gray-200 tw-overflow-hidden' style={{ borderRadius: 32 }}>
        <m.div
          layout
          role='group'
          id={`${id}-content`}
          aria-labelledby={`${id}-button`}
          inert={!isActive}
          className='tw-absolute tw-inset-y-0 tw-left-0 tw-w-full lg:tw-w-[var(--active-width)] lg:tw-left-[calc(50%-var(--active-width)/2)]'
        >
          <div className='tw-absolute tw-inset-0 lg:-tw-inset-x-4'>
            <SlideAsset
              slide={slide}
              isActive={isActive}
              onVideoElement={setVideoElement}
              onVideoPlayingChange={setIsVideoPlaying}
            />
            {noiseOverlayOpacity > 0 && (
              <Noise
                aria-hidden
                variant={noiseVariant}
                className='tw-pointer-events-none'
                style={{ opacity: noiseOverlayOpacity }}
              />
            )}
          </div>
          <SlideOverlay
            slide={slide}
            isActive={isActive}
            syncConversationToVideo={syncConversationToVideo}
            videoElement={videoElement}
          />
        </m.div>
      </m.div>
    </div>
  );
}

function SlideOverlay({
  slide,
  isActive,
  syncConversationToVideo,
  videoElement,
}: {
  slide: Slide;
  isActive: boolean;
  syncConversationToVideo: boolean;
  videoElement: HTMLVideoElement | null;
}) {
  if (slide.overlay.template === "conversation") {
    const messages = slide.overlay.conversation?.messages;
    if (!messages) {
      return null;
    }
    return (
      <AnimatePresence initial={false}>
        {isActive && (
          <ConversationOverlay
            messages={messages}
            syncConversationToVideo={syncConversationToVideo}
            videoElement={videoElement}
          />
        )}
      </AnimatePresence>
    );
  }

  return null;
}

const CONVERSATION_ASSISTANT_MESSAGE_VARIANTS = {
  variant: "elevated",
  tail: "left",
} satisfies ChatPlayerProps["assistantMessageVariants"];

const CONVERSATION_USER_MESSAGE_VARIANTS = {
  variant: "overlay",
  tail: "right",
} satisfies ChatPlayerProps["userMessageVariants"];

function ConversationOverlay({
  messages,
  syncConversationToVideo,
  videoElement,
}: {
  messages: ConversationMessages;
  syncConversationToVideo: boolean;
  videoElement: HTMLVideoElement | null;
}) {
  const isMobile = useIsMobile();
  const maxVisibleBubbles = isMobile ? MOBILE_MAX_VISIBLE_CHAT_BUBBLES : DESKTOP_MAX_VISIBLE_CHAT_BUBBLES;
  const source = useMemo(
    () => createChatPlayerSourceFromAccordionConversation(messages, { syncToVideo: syncConversationToVideo }),
    [messages, syncConversationToVideo],
  );

  const playback = useMemo(
    () =>
      syncConversationToVideo
        ? ({ type: "media", mediaElement: videoElement, fallbackTimeMs: 0 } as const)
        : ({ type: "auto", active: true } as const),
    [syncConversationToVideo, videoElement],
  );

  if (!source) {
    return null;
  }

  return (
    <m.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='tw-absolute tw-inset-0 tw-flex tw-items-end tw-justify-end tw-pb-1.5 tw-px-1.5 sm:tw-pb-3.5 sm:tw-px-3.5'
    >
      <div className='tw-h-full tw-min-h-0 tw-w-full tw-max-w-80'>
        <ChatPlayer
          source={source}
          anchor='bottom'
          playback={playback}
          animateInitialSnapshot={!syncConversationToVideo}
          assistantMessageVariants={CONVERSATION_ASSISTANT_MESSAGE_VARIANTS}
          userMessageVariants={CONVERSATION_USER_MESSAGE_VARIANTS}
          className='tw-size-full'
          maxVisibleBubbles={maxVisibleBubbles}
        />
      </div>
    </m.div>
  );
}
